import { twMerge } from 'tailwind-merge';
import { MONTHS_SHORT } from '@witmetrics/utils/constants';
import { fromBlankArray } from '@witmetrics/utils/arrays';
import Skeleton from '@/components/Skeleton';
import {
  AXIS_LABEL_SIZE,
  BAR_HEIGHTS,
  BAR_RADIUS,
  GRIDLINE_COUNT,
  LINE_POINTS,
  STAGGER,
} from './constants';
import { buildSmoothPath, classes, styles } from './utils';

export type ChartSkeletonProps = {
  showBars?: boolean;
  className?: string;
  categories?: readonly string[];
};

export default function ChartSkeleton({
  showBars = true,
  className,
  categories = MONTHS_SHORT,
}: ChartSkeletonProps) {
  const renderAxisLabel = (i: number) => (
    <div key={i} className={classes.axisLabel}>
      <Skeleton
        width={AXIS_LABEL_SIZE.width}
        height={AXIS_LABEL_SIZE.height}
        radius={BAR_RADIUS}
      />
    </div>
  );

  const renderGridline = (i: number) => (
    <div key={i} className={classes.gridline} />
  );

  const renderBar = (height: string, delay: number) => (
    <Skeleton
      className={classes.bar}
      delay={delay}
      height={height}
      radius={BAR_RADIUS}
    />
  );

  const renderBars = (category: string, i: number) => {
    const heights = BAR_HEIGHTS[i % BAR_HEIGHTS.length];
    return (
      <div key={category} className={classes.column}>
        {renderBar(heights[0], i * STAGGER)}
        {renderBar(heights[1], i * STAGGER)}
      </div>
    );
  };

  const renderLine = () => (
    <div className={classes.lineArea}>
      <Skeleton
        style={styles.areaFill}
        className={classes.areaFill}
        width="100%"
        height="100%"
        radius={0}
      />
      <svg
        className={classes.stroke}
        viewBox="0 0 100 100"
        preserveAspectRatio="none">
        <path
          className={classes.strokePath}
          d={buildSmoothPath(LINE_POINTS)}
          vectorEffect="non-scaling-stroke"
        />
      </svg>
    </div>
  );

  const renderCategoryLabel = (category: string) => (
    <span key={category} className={classes.label}>
      {category}
    </span>
  );

  return (
    <div className={twMerge(classes.wrapper, className)}>
      <div className={classes.axis}>
        {fromBlankArray(GRIDLINE_COUNT, renderAxisLabel)}
      </div>
      <div className={classes.plot}>
        <div className={classes.gridlines}>
          {fromBlankArray(GRIDLINE_COUNT, renderGridline)}
        </div>
        {showBars ? (
          <div className={classes.columns}>{categories.map(renderBars)}</div>
        ) : (
          renderLine()
        )}
      </div>
      <div />
      <div className={classes.labels}>
        {categories.map(renderCategoryLabel)}
      </div>
    </div>
  );
}
